// composables/useActAs.js
//
// The enter / exit / switch state machine for "act as a member" (operator
// impersonation) — PWA-only.
//
// THE #1 CORRECTNESS RULE (reload discipline): exchange (enter) and leave (exit)
// change WHO the Sanctum session cookie authenticates as, server-side. Every
// identity-scoped value the PWA holds — Pinia stores, pinia-orm repositories,
// the Framework7 view stack — belongs to the PREVIOUS principal and is now
// wrong. So after a successful exchange/leave we:
//   1. purge identity storage (clear the store + remove `poolit:auth`), then
//   2. hard-reload.
// The reload wipes all in-memory state; removing `poolit:auth` FIRST stops
// cold-boot Priority 2 from instantly painting the previous principal (stale
// flash) — with it gone, Priority 3 cookie-bootstrap re-derives the store as
// the NEW principal from a fresh `/profile` fetch. We never hot-swap identity
// in place. See act-as plan Section 3.
//
// Non-goals here: the picker sheet, the ProfilePage entry, and the acting-as
// banner/overlay are separate components (FE-P2/P3). This composable owns only
// the flow + purge and exposes a shared `switching` flag the overlay renders.
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import i18n from '@/i18n';
import { isPwa } from '@/platform/index.js';
import { actAs, exchange, leave } from '@/services/launchToken.service.js';
import { AUTH_PERSIST_KEY, useAuthStore } from '@/stores/authStore.js';

const t = (key) => i18n.global.t(key);

// Push snooze key (per-device, not per-user) — cleared on identity swap so the
// operator's snooze window doesn't bleed into the target and vice-versa. Kept
// in sync with PRIMING_SNOOZE_KEY in usePushNotifications.js.
const PUSH_SNOOZE_KEY = 'poolit:push-snooze-until';

// Read on the next boot by the picker host: when set, auto-reopen the picker
// after a "switch member" leave-and-reload.
const ACT_AS_REOPEN_KEY = 'poolit:act-as-reopen';

// Toast copy keyed by act-as / exchange failure status. 401 is intentionally
// absent — it flows to the global auth:unauthorized interceptor (redirect to
// /login), so we neither toast nor reload on it.
const ENTER_ERROR_KEYS = {
  400: 'actAs.toast.unavailable',
  403: 'actAs.toast.unavailable',
  409: 'actAs.toast.noOwner',
  410: 'actAs.toast.expired',
};

// Module-scoped so the trigger (picker) and the renderer (overlay) share one
// instance regardless of where useActAs() is called.
const switching = ref(false);

function toast(text) {
  try {
    f7.toast.create({ text, position: 'top', closeTimeout: 4000 }).open();
  } catch {
    /* f7 not mounted (tests / early boot) — toast is best-effort */
  }
}

/**
 * Isolated so tests can stub it (window.location.reload is awkward to spy on
 * directly). Guarded for SSR/test environments without a live location.
 * Exported for the collector's supplier preview (useSupplierPreview), which
 * swaps what the session shows the same way act-as swaps who it is.
 */
export function hardReload() {
  if (typeof window !== 'undefined' && window.location?.reload) {
    window.location.reload();
  }
}

/**
 * A hard load of ANOTHER path — what a boot that must land elsewhere needs
 * instead of a reload. On a reload the browser keeps the history entry's state,
 * and Framework7 (History.init reads it when the app starts, before App.vue's
 * boot rewrites the address bar) matches it against the router history it keeps
 * in localStorage and restores the page that was on screen — so a reload on
 * /collector/shops/73/ rewritten to /suppliers/ still paints the shop page
 * (router-class.js getInitialUrl). `location.replace` starts a fresh entry with
 * no state; F7 then boots from the address bar. Isolated so tests can stub it.
 */
export function hardNavigate(path) {
  if (typeof window !== 'undefined' && window.location?.replace) {
    window.location.replace(path);
  }
}

export function useActAs() {
  const authStore = useAuthStore();

  /**
   * The operator entry (ProfilePage "Act as a member") shows only when the user
   * holds act-as rights AND we're in the PWA. TMA is excluded: after purge +
   * reload, auth-init Priority 1 compares the operator's Telegram id against the
   * (now-null) stored id, re-authenticates from init_data, and destroys the
   * impersonation session — TMA re-auth fundamentally fights session-swap
   * (plan Section 3.2). The banner still reads the store as a harmless safety net if a
   * TMA session is ever impersonating.
   */
  const canEnter = computed(() => authStore.canActAs && isPwa());

  const isImpersonating = computed(() => authStore.isImpersonating);
  const actingAsName = computed(() => authStore.actingAs?.name ?? null);
  const operatorName = computed(() => authStore.operator?.name ?? null);

  /**
   * Drop every identity-scoped storage key so cold-boot re-derives the new
   * principal cleanly. Deliberately NO `/logout` call — the session is being
   * SWAPPED (exchange/leave already changed it server-side), not ended.
   *   - clearSession(): wipe the in-memory store immediately.
   *   - remove `poolit:auth`: stop Priority 2 painting the previous principal.
   *   - remove push snooze: don't carry one identity's snooze to the other.
   * (No `/api` SW cache exists to purge — verified in the plan Section 3.1.)
   */
  async function purgeIdentityStorage() {
    // Tear the realtime socket down BEFORE the identity swaps. The hardReload()
    // that follows every purge would drop the socket on unload anyway, but an
    // explicit teardown leaves every channel cleanly under the OLD principal
    // instead of racing the unload — nothing lingers subscribed as the wrong
    // identity, and the behaviour holds even where the reload is deferred. Re-init
    // runs on the next boot (App.vue) as the NEW principal.
    //
    // Lazy-imported so realtime/echo.js (and its realtimeStore.js `defineStore`
    // side-effect) stays OUT of this composable's static graph — useActAs is
    // pulled into AppLayout/banner graphs, and a static import broke every
    // partial-pinia-mock test that transitively renders them. Awaited so teardown
    // completes before the reload; best-effort: never block the purge/reload.
    try {
      const { teardownRealtime } = await import('@/realtime/echo.js');
      teardownRealtime();
    } catch (err) {
      console.warn('[useActAs] teardownRealtime failed:', err);
    }
    authStore.clearSession();
    try {
      localStorage.removeItem(AUTH_PERSIST_KEY);
    } catch {
      /* storage blocked (private mode/quota) — store clear already ran */
    }
    try {
      localStorage.removeItem(PUSH_SNOOZE_KEY);
    } catch {
      /* noop */
    }
  }

  /**
   * Map an act-as/exchange failure to a toast + a `needsRefresh` signal for the
   * picker (the pick may be stale). 401 is left for the global handler.
   */
  function handleEnterError(error) {
    const status = error?.status ?? 0;
    if (status === 401) {
      // Global auth:unauthorized interceptor already handles the redirect.
      return { ok: false, needsRefresh: false, status };
    }
    toast(t(ENTER_ERROR_KEYS[status] || 'actAs.toast.unavailable'));
    // Any non-401 failure (out-of-scope 403, malformed 400, no-owner 409,
    // expired-token 410, network 0) means the operator should re-fetch and pick
    // again — never reload on these.
    return { ok: false, needsRefresh: true, status };
  }

  /**
   * Enter impersonation: mint a token for the target membership, exchange it
   * (session becomes the target), then purge + hard-reload as the new principal.
   *
   * @param {string|number} membershipId
   * @returns {Promise<{ ok: boolean, needsRefresh?: boolean, status?: number }>}
   *   `{ ok: true }` right before the reload; on failure `ok:false` with a
   *   `needsRefresh` hint and the HTTP status (no reload).
   */
  async function enter(membershipId) {
    try {
      const { token } = await actAs({ membershipId });
      await exchange({ token });
    } catch (error) {
      return handleEnterError(error);
    }

    // Success — swap identity. Flip the overlay, purge, then reload.
    switching.value = true;
    await purgeIdentityStorage();
    hardReload();
    return { ok: true };
  }

  /**
   * Exit impersonation: leave (restore the operator), then purge + reload.
   * `leave` is best-effort + idempotent (204 even when not impersonating); a
   * network/other failure still purges + reloads so the operator is never
   * stranded behind the overlay — Priority 3 re-derives delegation on boot, so
   * a genuinely-still-impersonating session simply re-shows the banner to retry.
   */
  async function exit() {
    switching.value = true;
    try {
      await leave();
    } catch (error) {
      console.warn('[useActAs] leave() failed, reloading anyway:', error);
    }
    await purgeIdentityStorage();
    hardReload();
  }

  /**
   * Switch to a different member: leave first (the target's own scope is not the
   * operator's), flag the next boot to auto-reopen the picker, then purge +
   * reload. Modeled as exit-then-repick because `/act-as/available` returns the
   * operator's scope only when authenticated AS the operator.
   */
  async function switchMember() {
    switching.value = true;
    try {
      await leave();
    } catch (error) {
      console.warn('[useActAs] leave() (switch) failed, reloading anyway:', error);
    }
    try {
      sessionStorage.setItem(ACT_AS_REOPEN_KEY, '1');
    } catch {
      /* sessionStorage blocked — the picker just won't auto-reopen */
    }
    await purgeIdentityStorage();
    hardReload();
  }

  return {
    // gates + display
    canEnter,
    isImpersonating,
    actingAsName,
    operatorName,
    // overlay flag (rendered by the switching overlay in FE-P2)
    switching,
    // flow
    enter,
    exit,
    switchMember,
    purgeIdentityStorage,
  };
}

// Exported for the picker host to consume + clear on the auto-reopen boot.
export { ACT_AS_REOPEN_KEY };
